import { cn } from "@/lib/utils"

const SIZE = {
  default: {
    root: "px-4 py-3 gap-3 rounded-2xl",
    icon: "h-8 w-8",
    title: "text-[13px]",
    subtitle: "text-[11.5px]",
  },
  compact: {
    root: "min-w-[240px] px-2 py-1.5 gap-2 rounded-lg",
    icon: "h-[22px] w-[22px] text-[12px]",
    title: "text-xs",
    subtitle: "text-[10.5px] opacity-75",
  },
} as const

interface HorizontalCardProps {
  icon: React.ReactNode
  title: string
  subtitle?: string
  trailingElement?: React.ReactNode
  isActive?: boolean
  isComplete?: boolean
  size?: keyof typeof SIZE
  onClick?: () => void
  /** Root-level overrides only — avoid re-specifying colors or text sizes */
  className?: string
  disabled?: boolean
}

export function HorizontalCard({
  icon,
  title,
  subtitle,
  trailingElement,
  isActive = false,
  isComplete = false,
  size = "default",
  onClick,
  className,
  disabled,
}: HorizontalCardProps) {
  const s = SIZE[size]

  const iconBubble = isActive
    ? "bg-yellow1 text-black"
    : isComplete
      ? "bg-yellow1/20 text-yellow1"
      : "bg-grey3 text-grey5"

  const titleColor = isActive || isComplete ? "text-white" : "text-grey6"

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex w-full items-center border bg-card text-left transition-colors",
        s.root,
        isActive
          ? "border-yellow2 bg-yellow1/12 hover:bg-yellow1/15"
          : isComplete
            ? "border-yellow2/50 bg-yellow1/[0.07] hover:bg-yellow1/10"
            : "border-grey4 hover:bg-white/4",
        className,
      )}
    >
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full",
          s.icon,
          iconBubble,
        )}
      >
        {icon}
      </span>

      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "truncate font-semibold leading-snug",
            s.title,
            titleColor,
          )}
        >
          {title}
        </div>
        {subtitle && (
          <div className={cn("truncate leading-snug text-grey5", s.subtitle)}>
            {subtitle}
          </div>
        )}
      </div>

      {trailingElement}
    </button>
  )
}
